// src/pages/ParametresQuotas.js - Configuration admin des prix unitaires des
// quotas (client/site/tag_qr/tag_nfc) achetés en complément d'un abonnement.
// Avant cette page, ces montants étaient codés en dur dans le backend
// (achat_quota/create.php) et dans le frontend (AchatQuota.js) — nulle part
// dans l'admin pour les modifier.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

const TYPES = [
  { key: 'client', label: 'Clients', icon: 'bi-people', color: '#2196f3' },
  { key: 'site', label: 'Sites', icon: 'bi-building', color: COLORS.success },
  { key: 'tag_qr', label: 'Tags QR', icon: 'bi-qr-code', color: COLORS.warning },
  { key: 'tag_nfc', label: 'Tags NFC', icon: 'bi-wifi', color: '#9c27b0' },
];

const ParametresQuotas = () => {
  const [prix, setPrix] = useState({ client: '', site: '', tag_qr: '', tag_nfc: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // La lecture (quota_prix/list.php) est ouverte à tout profil authentifié par
  // design, mais cette page de configuration elle-même (modification des
  // tarifs, quota_prix/update.php) est admin-only côté backend — ce garde
  // évite d'afficher une page de config à un non-admin qui ne peut de toute
  // façon rien y faire.
  let currentProfile = 0;
  try {
    currentProfile = Number(JSON.parse(localStorage.getItem("user") || "{}")?.profile || 0);
  } catch (e) { /* ignore */ }
  const isAdmin = currentProfile === 1;

  const fetchPrix = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API_BASE_URL}/quota_prix/list.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setPrix(res.data.prix);
      } else {
        setError(res.data?.message || "Erreur lors du chargement.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger les tarifs des quotas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchPrix();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPrix]);

  if (!isAdmin) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
            Tarifs des quotas
          </h1>
        </div>
        <section className="section">
          <div className="alert alert-warning">
            Accès réservé à l'administrateur.
          </div>
        </section>
      </Layout>
    );
  }

  const handleChange = (key, value) => {
    setPrix(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.post(
        `${API_BASE_URL}/quota_prix/update.php`,
        { prix },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (res.data?.success) {
        setSuccessMsg("Tarifs des quotas enregistrés avec succès.");
        fetchPrix();
        setTimeout(() => setSuccessMsg(""), 4000);
      } else {
        setError(res.data?.message || "Erreur lors de l'enregistrement.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '700', fontSize: '1.8rem' }}>
          <i className="bi bi-tags me-2"></i>
          Tarifs des quotas
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Tarifs des quotas</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="alert alert-info d-flex align-items-start mb-4" style={{ borderRadius: '10px' }}>
          <i className="bi bi-info-circle me-2 mt-1"></i>
          <div>
            Ces prix unitaires sont utilisés partout où une société peut acheter des quotas
            supplémentaires (page "Achat de quotas"). Une modification s'applique immédiatement
            aux nouveaux achats — les achats déjà effectués ne sont pas recalculés.
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4">
            {error}
            <button type="button" className="btn-close" onClick={() => setError("")}></button>
          </div>
        )}
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-4">
            {successMsg}
            <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: COLORS.primary }} role="status"></div>
          </div>
        ) : (
          <form onSubmit={handleSave}>
            <div className="row">
              {TYPES.map(({ key, label, icon, color }) => (
                <div key={key} className="col-md-6 col-lg-3 mb-4">
                  <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px', borderTop: `4px solid ${color}` }}>
                    <div className="card-body">
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{
                          width: '36px', height: '36px', backgroundColor: `${color}18`, color
                        }}>
                          <i className={`bi ${icon}`}></i>
                        </div>
                        <span style={{ color: COLORS.darkText, fontWeight: '600' }}>{label}</span>
                      </div>
                      <div className="input-group">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="form-control"
                          value={prix[key] ?? ''}
                          onChange={(e) => handleChange(key, e.target.value)}
                          style={{ borderRadius: '8px 0 0 8px' }}
                        />
                        <span className="input-group-text" style={{ borderRadius: '0 8px 8px 0' }}>FCFA</span>
                      </div>
                      <small className="text-muted d-block mt-2">Prix par unité</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="btn fw-semibold"
              disabled={saving}
              style={{
                backgroundColor: COLORS.primary,
                color: COLORS.white,
                borderRadius: '8px',
                padding: '10px 32px',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Enregistrement...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Enregistrer les tarifs
                </>
              )}
            </button>
          </form>
        )}
      </section>
    </Layout>
  );
};

export default ParametresQuotas;
